export type WebCitationSource = {
  citationKey: string;
  title?: string;
  url: string;
};

const CITATION_KEY_PATTERN = /\[(S\d{1,2})\]/g;

type LoosePart = { type: string } & Record<string, unknown>;

function readString(record: Record<string, unknown>, key: string): string | undefined {
  const value = record[key];
  return typeof value === 'string' && value.trim() ? value : undefined;
}

/**
 * Collecte les sources web citables d'un message : parts `source-url` persistées
 * (après rechargement) et outputs `tool-fetch_url` en direct (pendant le stream).
 */
export function collectWebCitationSources(parts: ReadonlyArray<LoosePart>): WebCitationSource[] {
  const sources = new Map<string, WebCitationSource>();

  for (const part of parts) {
    if (part.type === 'source-url') {
      const citationKey = readString(part, 'citationKey');
      const url = readString(part, 'url');
      if (citationKey && url && !sources.has(citationKey)) {
        sources.set(citationKey, {
          citationKey,
          title: readString(part, 'title'),
          url,
        });
      }
      continue;
    }

    if (part.type === 'tool-fetch_url' && part.state === 'output-available') {
      const output = part.output;
      if (!output || typeof output !== 'object' || Array.isArray(output)) {
        continue;
      }
      const record = output as Record<string, unknown>;
      if (record.error === true) {
        continue;
      }
      const citationKey = readString(record, 'citationKey');
      const url = readString(record, 'finalUrl') ?? readString(record, 'url');
      if (citationKey && url && !sources.has(citationKey)) {
        sources.set(citationKey, {
          citationKey,
          title: readString(record, 'title'),
          url,
        });
      }
    }
  }

  return [...sources.values()];
}

/**
 * Remplace les clés de citation `[S1]` du texte assistant par des liens Markdown
 * vers la source correspondante. Les clés inconnues restent en texte brut.
 */
export function linkifyWebCitations(text: string, sources: WebCitationSource[]): string {
  if (!text || sources.length === 0) {
    return text;
  }

  const byKey = new Map(sources.map((source) => [source.citationKey, source]));

  return text.replace(CITATION_KEY_PATTERN, (match, key: string) => {
    const source = byKey.get(key);
    if (!source) {
      return match;
    }
    const title = source.title?.replace(/"/g, '″');
    return title
      ? `[\\[${key}\\]](${source.url} "${title}")`
      : `[\\[${key}\\]](${source.url})`;
  });
}
